import { useListConversations } from "@/tanstack-hooks";
import Spinner from "../Spinner";
import EmptyState from "../EmptyState";
import { CloudOffIcon } from "lucide-react";
import { ConversationResponse } from "@/generated-api";
import chatIcon from "../../assets/chats.svg";
import { useEffect, useRef, useState } from "react";
import { useInView } from "react-intersection-observer";
import SpinnerSmall from "../SpinnerSmall";
import ConversationRow from "./ConversationRow";
import ScrollTopButton from "../ScrollTopButton";
import { useLocation, useNavigate } from "react-router-dom";
import { useSwipeCoordination } from "@/hooks/useSwipeCoordination";
import { useTranslation } from "@/hooks/useTranslation";

export default function MobileConversationList({
  onSelectConversation,
  onDeleteConversation,
}: {
  onSelectConversation: (id: ConversationResponse) => void;
  onDeleteConversation: (counterpartyId: string) => void;
}) {
  const { ref, inView } = useInView({
    rootMargin: "400px 0px",
    threshold: 0,
  });
  const {
    data,
    isLoading,
    isError,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useListConversations();
  const location = useLocation();
  const [isInitialDelay, setIsInitialDelay] = useState(true);
  const [isLoaded, setIsLoaded] = useState(false);
  const fetchTimeoutRef = useRef<NodeJS.Timeout>();
  const pageRef = useRef<HTMLDivElement>(null);
  const { handleSwipeStateChange, handleInteraction } = useSwipeCoordination();
  const navigate = useNavigate();
  const t = useTranslation();

  const handleRowClick = (c: ConversationResponse) => {
    onSelectConversation(c);
    if (pageRef.current) {
      sessionStorage.setItem(location.pathname, window.scrollY.toString());
    }
  };

  function handleAvatarClick(c: ConversationResponse) {
    if (pageRef.current) {
      sessionStorage.setItem(location.pathname, window.scrollY.toString());
    }
    navigate("/profile/" + c.counterparty.counterparty_id);
  }

  useEffect(() => {
    const savedScroll = sessionStorage.getItem(location.pathname);
    if (savedScroll !== null) {
      const timer = setTimeout(() => {
        window.scrollTo(0, parseInt(savedScroll));
        sessionStorage.removeItem(location.pathname);
        setIsLoaded(true);
      }, 50);
      return () => clearTimeout(timer);
    } else {
      // If no saved scroll, we can show content immediately
      setIsLoaded(true);
    }
  }, [data]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setIsInitialDelay(false);
    }, 500);

    return () => clearTimeout(timer);
  }, []);

  // Debounced fetch next page
  useEffect(() => {
    if (inView && hasNextPage && !isFetchingNextPage && !isInitialDelay) {
      // Clear any existing timeout
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }

      // Set a new timeout for fetching the next page
      fetchTimeoutRef.current = setTimeout(() => {
        void fetchNextPage();
      }, 250);
    }

    return () => {
      if (fetchTimeoutRef.current) {
        clearTimeout(fetchTimeoutRef.current);
      }
    };
  }, [inView, hasNextPage, isFetchingNextPage, fetchNextPage, isInitialDelay]);

  if (isLoading) {
    return (
      <div className="flex flex-col items-center justify-center h-[80vh] w-full mx-auto">
        <Spinner />
      </div>
    );
  }

  if (isError || !data) {
    return (
      <EmptyState
        icon={CloudOffIcon}
        title={t.MESSAGES.ERRORS.LOADING}
        message={t.MESSAGES.ERRORS.LOADING_DESCRIPTION}
      />
    );
  }

  const conversations = data.pages.flatMap((page) => page.items);

  if (conversations.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center h-[80vh] w-full max-w-5xl mx-auto -mt-32">
        <img src={chatIcon} className="h-16 w-16 text-gray-300 mb-4" />
        <h2 className="text-2xl font-semibold text-gray-400 mb-2">
          {t.MESSAGES.LABELS.NO_CONVERSATIONS_TITLE}
        </h2>
        <p className="text-gray-500 text-center max-w-md">
          {t.MESSAGES.LABELS.NO_CONVERSATIONS_DESCRIPTION}
        </p>
      </div>
    );
  }

  return (
    <div
      className={`dark border-t border-zinc-600 h-full w-full
    transition-opacity duration-50 ${isLoaded ? "opacity-100" : "opacity-0"}
    `}
    >
      <div className="py-2" ref={pageRef}>
        {conversations.map((conversation) => (
          <ConversationRow
            key={conversation.conversation_id}
            conversation={conversation}
            selectedConversationId={undefined}
            onDelete={onDeleteConversation}
            onClick={() => {
              handleInteraction();
              handleRowClick(conversation);
            }}
            onSwipeStateChange={(isOpen) =>
              handleSwipeStateChange(conversation.conversation_id, isOpen)
            }
            onInteraction={handleInteraction}
            onAvatarClick={() => handleAvatarClick(conversation)}
          />
        ))}
      </div>
      {hasNextPage && (
        <div
          ref={ref}
          className="w-full h-10 py-4 flex items-center justify-center"
        >
          {isFetchingNextPage && <SpinnerSmall />}
        </div>
      )}
      <ScrollTopButton />
    </div>
  );
}
